import {useMemo } from 'react';
import PropTypes from 'prop-types';

import ReservationsPanelSearch from './ReservationsPanelSearch';
import {
  ErrorNotice,
  NoDataNotice
} from '@/libs/components/common/Notices';

import "@esri/calcite-components/components/calcite-list";
import "@esri/calcite-components/components/calcite-list-item";
import "@esri/calcite-components/components/calcite-panel";
import "@esri/calcite-components/components/calcite-button";
import "@esri/calcite-components/components/calcite-action";
import "@esri/calcite-components/components/calcite-label";

import '@styles/mapPanels.css';
import './ReservationsPanel.css';

const ReservationsPanel = ({
  allReservations,
  reservationsByCluster = {},
  selectedReservations,
  selectedClusters,
  onSelectAll,
  onDeselectAll,
  onZoomSelReservations,
  onZoomReservation,
  onContinue,
  error,
  setError,
  ariaLive = "off",
  recentReservations = [],
  reservationList = [],
  handleListItemSelect,
  handleSearchSelect,
  handleClusterSelect
}) => {
  const resvClusters = useMemo(
    () => Object.keys(reservationsByCluster),
    [reservationsByCluster]
  );

  return (
    <calcite-panel
      className="reservations-panel"
      heading="Select Reservations"
      aria-label="Reservations selection panel"
    >
      <h2
        className="sr-only"
        id="reservations-panel-heading"
      >
        Reservations Selection Panel
      </h2>

      <div className="reservations-content">

        {/* Error Notice */}
        {error && (
          <div
            aria-live={ariaLive}
            role="alert"
          >
            <ErrorNotice
              message={error}
              onCalciteNoticeClose={() =>
                setError('')
              }
              closable
            />
          </div>
        )}

        <ReservationsPanelSearch
          allReservations={allReservations}
          onSelect={handleSearchSelect}
        />

        <calcite-label
          layout="inline"
          id="cluster-heading"
        >
          Filter by Cluster
        </calcite-label>

        <div
          className="tribal-clusters"
          role="group"
          aria-labelledby="cluster-heading"
        >
          {resvClusters.map((cluster) => (
            <calcite-button
              key={cluster}
              onClick={(e) =>
                handleClusterSelect(
                  e,
                  cluster
                )
              }
              appearance={
                selectedClusters.includes(
                  cluster
                )
                  ? "solid"
                  : "outline"
              }
              kind="brand"
              scale="s"
              aria-pressed={selectedClusters.includes(
                cluster
              )}
              aria-label={`Filter reservations by cluster ${cluster}`}
            >
              {cluster}
            </calcite-button>
          ))}
        </div>

        <calcite-label
          layout="inline"
          id="reservations-list-heading"
        >
          Reservations List
        </calcite-label>

        <div className="reservations-list-wrapper">
          <calcite-list
            aria-labelledby="reservations-list-heading"
            role="list"
          >
            {reservationList.map((res) => (
              <calcite-list-item
                key={res.id}
                label={res.name}
                description={
                  selectedReservations.includes(
                    res.id
                  )
                    ? (() => {
                        const associatedClusters =
                          selectedClusters.filter(
                            (cluster) =>
                              (
                                reservationsByCluster[
                                  cluster
                                ] || []
                              ).some(
                                (r) =>
                                  r.id ===
                                  res.id
                              )
                          );

                        return associatedClusters.length
                          ? `Associated with ${associatedClusters.join(', ')}`
                          : '';
                      })()
                    : ''
                }
                selected={selectedReservations.includes(
                  res.id
                )}
                oncalcitelistitemselect={() =>
                  handleListItemSelect(
                    res.id
                  )
                }
                tabIndex={0}
                role="listitem"
                aria-label={`Reservation ${res.name}${
                  selectedReservations.includes(
                    res.id
                  )
                    ? ', selected'
                    : ''
                }`}
              >
                {res.location && (
                  <calcite-action
                    slot="actions-end"
                    icon="zoom-to-object"
                    text="Zoom to reservation"
                    aria-label={`Zoom to reservation ${res.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onZoomReservation(
                        res
                      );
                    }}
                  />
                )}
              </calcite-list-item>
            ))}
          </calcite-list>
        </div>

        <div
          className="master-controls"
          role="group"
          aria-label="Reservation selection controls"
        >
          <calcite-button
            onClick={onSelectAll}
            scale="s"
            appearance="outline"
            className="reservationBtn"
          >
            Select All
          </calcite-button>

          <calcite-button
            onClick={onDeselectAll}
            scale="s"
            appearance="outline"
            className="reservationBtn"
          >
            Deselect All
          </calcite-button>

          <calcite-button
            onClick={onZoomSelReservations}
            scale="s"
            appearance="outline"
            className="reservationBtn"
          >
            Zoom Selected
          </calcite-button>
        </div>

        <div className="reservations-footer">
          <div className="footer-flex-row">

            <calcite-button
              scale="s"
              appearance="outline"
              kind="brand"
              className="continue-button"
              disabled={
                selectedReservations.length === 0
              }
              onClick={() =>
                onContinue({
                  ids: selectedReservations
                })
              }
            >
              Continue with Selected (
              {selectedReservations.length})
            </calcite-button>

            {!recentReservations ||
            recentReservations.length === 0 ? (
              <NoDataNotice
                message="No recent reservations available"
                scale="s"
              />
            ) : (
              <div className="recent-list-content">
                <calcite-label
                  layout="inline"
                  id="recent-list-heading"
                >
                  Most Recently Visited
                  Clusters or Reservations
                </calcite-label>

                {recentReservations.map(
                  (item, idx) => (
                    <calcite-button
                      key={`recent-${item.length}${idx}`}
                      scale="s"
                      appearance="outline"
                      kind="brand"
                      className="continue-button"
                      onClick={() =>
                        onContinue({
                          ids: item
                        })
                      }
                      aria-label={`Continue with ${item.length} reservations`}
                    >
                      {`Continue with ${item.length} reservations`}
                    </calcite-button>
                  )
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    </calcite-panel>
  );
};

ReservationsPanel.propTypes = {
  allReservations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
      ]).isRequired,
      name: PropTypes.string.isRequired,
      location: PropTypes.shape({
        lng: PropTypes.number,
        lat: PropTypes.number
      }),
    })
  ).isRequired,

  reservationsByCluster: PropTypes.object
    .isRequired,

  selectedReservations: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.object
    ])
  ).isRequired,

  selectedClusters: PropTypes.arrayOf(
    PropTypes.string
  ).isRequired,

  onSelectAll: PropTypes.func.isRequired,
  onDeselectAll: PropTypes.func.isRequired,
  onZoomSelReservations:
    PropTypes.func.isRequired,
  onZoomReservation:
    PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,

  error: PropTypes.string,
  setError: PropTypes.func.isRequired,
  ariaLive: PropTypes.string,

  recentReservations: PropTypes.arrayOf(
    PropTypes.array
  ),

  reservationList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
      ]).isRequired,
      name: PropTypes.string.isRequired,
      location: PropTypes.object
    })
  ).isRequired,

  handleListItemSelect:
    PropTypes.func.isRequired,
  handleSearchSelect:
    PropTypes.func.isRequired,
  handleClusterSelect:
    PropTypes.func.isRequired,
};

export default ReservationsPanel;